import { useEffect, useRef, useState } from 'react';

import FormGroup from './FormGroup';
import InsurancePickerModal from './InputPersonalInsurancePickerModal';

import { useScreenSizeCheck } from '../Helpers/screen-size-helpers';
import insuranceTypes from '../data/personalInsurancePickerData';
import DropdownArrowSVG from './ui/assets/dropdown-arrow-down.svg';

const getElements = () => [
    document.querySelector('header'),
    document.getElementById('form-layout-bottom-container'),
    document.getElementById('footer-bar'),
    document.querySelector('footer'),
];
const changeVisibility = (visibility, elements) => {
    elements.forEach(element => {
        if (element) {
            element.style.visibility = visibility;
        }
    });
};

/*==========================*
 | INSURANCE PICKER Button  |
 *==========================*/

const InputInsurancePicker = ({
    name,
    value,
    isRequired,
    showError,
    externalErrorMessage,
    placeholder,
    placeholderMobile,
    onChange = () => {},
    inlineStyle = {},
    label,
}) => {
    const [isValid, setIsValid] = useState(true);
    const [selection, setSelection] = useState(value || []);
    const pickerButtonRef = useRef(null);
    const [showpickerModal, setShowInsurancePicker] = useState(false);
    const minTabletScreen = 481;
    const isLargeScreen = useScreenSizeCheck(minTabletScreen);

    useEffect(() => {
        const elements = getElements();

        if (showpickerModal && !isLargeScreen) {
            document.body.style.overflow = 'hidden';
            // Prevent overlapping elements
            changeVisibility('hidden', elements);
        } else {
            document.body.style.overflow = '';
            // Reset overlapping elements
            changeVisibility('visible', elements);
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [showpickerModal, isLargeScreen]);

    const checkSelectionValid = selection => {
        let valid = true;
        if (isRequired) {
            valid = selection.length > 0;
        }
        return valid;
    };

    const toggleInsurancePicker = () => {
        setTimeout(() => {
            setShowInsurancePicker(!showpickerModal);
            if (showpickerModal) pickerButtonRef.current.focus();
        }, 1);
    };

    const handleSelectionChange = nextSelection => {
        const valid = checkSelectionValid(nextSelection);
        setIsValid(valid);
        setSelection(nextSelection);
        // Emulate dom element for form data
        const pickerElement = pickerButtonRef.current;
        pickerElement.value = nextSelection;
        onChange(
            {
                target: {
                    value: nextSelection,
                    focus: () => {
                        pickerElement.focus();
                    },
                },
            },
            valid
        );
    };

    const hasError = showError && (!isValid || externalErrorMessage);
    const hasSelection = selection.length > 0;

    return (
        <FormGroup
            label={label}
            name={name}
            showError={showError}
            externalErrorMessage={externalErrorMessage}
            isValid={isValid}
            noWrapLabel={true}
            required={isRequired}
        >
            <div className="insurance-picker">
                <button
                    ref={pickerButtonRef}
                    type="button"
                    name={name}
                    id={name}
                    style={inlineStyle}
                    className={`insurance-picker form-control${hasError ? ' error' : ''}`}
                    data-testid="insurance-picker"
                    required={isRequired}
                    aria-expanded={showpickerModal}
                    aria-label="Select from a list of insurance types"
                    aria-describedby={hasError ? `${name}-error` : null}
                    onClick={toggleInsurancePicker}
                >
                    {!hasSelection && (
                        <>
                            <span className="insurance-picker-placeholder-tablet">{placeholder}</span>
                            <span className="insurance-picker-placeholder-mobile">
                                {placeholderMobile ? placeholderMobile : placeholder}
                            </span>
                        </>
                    )}
                    {hasSelection && (
                        <span className="insurance-picker-selection">
                            {selection.map(selected => selected.name).join(', ')}
                        </span>
                    )}
                    <img alt="" src={DropdownArrowSVG} height="22px" width="22px" loading="lazy" />
                </button>
                {showpickerModal && (
                    <InsurancePickerModal
                        insuranceTypes={insuranceTypes}
                        setInsurance={setSelection}
                        onCancel={toggleInsurancePicker}
                        onAccept={toggleInsurancePicker}
                        useSelection={() => [selection, handleSelectionChange]}
                    />
                )}
            </div>
        </FormGroup>
    );
};

export default InputInsurancePicker;
